// /opt/isp_erp/frontend/src/pages/Roles.jsx

import { useEffect, useState } from "react";
import {
  getRoles,
  createRole,
  assignRoleToUser,
  deleteRole,
  getUsuarios,
  removeRoleFromUser,
} from "../api/roles.api";
import "./Roles.css";

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [nuevoRol, setNuevoRol] = useState("");
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState("");
  const [rolSeleccionado, setRolSeleccionado] = useState("");
  const [loading, setLoading] = useState(false);

  // =========================
  // CARGA INICIAL
  // =========================
  useEffect(() => {
    fetchRoles();
    fetchUsuarios();
  }, []);

  const fetchRoles = async () => {
    try {
      const data = await getRoles();
      setRoles(data);
    } catch (err) {
      console.error("Error al obtener roles:", err);
    }
  };

  const fetchUsuarios = async () => {
    try {
      const data = await getUsuarios();
      setUsuarios(data);
    } catch (err) {
      console.error("Error al obtener usuarios:", err);
    }
  };

  // =========================
  // ACCIONES
  // =========================
  const handleCrearRol = async () => {
    if (!nuevoRol.trim()) return;
    setLoading(true);
    try {
      await createRole({ name: nuevoRol });
      setNuevoRol("");
      fetchRoles();
    } catch (err) {
      console.error("Error al crear rol:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAsignarRol = async () => {
    if (!usuarioSeleccionado || !rolSeleccionado) return;
    setLoading(true);
    try {
      await assignRoleToUser(usuarioSeleccionado, rolSeleccionado);
      alert("✅ Rol asignado correctamente");
      setRolSeleccionado("");
      fetchUsuarios();
    } catch (err) {
      console.error("Error al asignar rol:", err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Eliminar rol de un usuario (con confirmación especial para admin)
  const handleRemoveRoleFromUser = async (userId, roleId, roleName, username) => {
    let confirmMessage = `¿Deseas eliminar el rol "${roleName}" del usuario ${username}?`;
    
    if (roleName === 'admin') {
      const adminCount = usuarios.filter(u => 
        u.roles?.some(r => r.name === 'admin')
      ).length;
      
      if (adminCount <= 1) {
        alert("⚠️ No se puede eliminar el último rol admin del sistema.");
        return;
      }
      
      confirmMessage = `⚠️ ADVERTENCIA: Estás a punto de eliminar el rol ADMIN del usuario ${username}. 
      ¿Estás seguro? (Asegúrate de que haya otro usuario con rol admin)`;
    }
    
    if (!window.confirm(confirmMessage)) return;

    setLoading(true);
    try {
      await removeRoleFromUser(userId, roleId);
      alert(`✅ Rol "${roleName}" eliminado de ${username}`);
      fetchUsuarios();
    } catch (err) {
      console.error("Error al eliminar rol:", err);
      alert(err.response?.data?.detail || "Error al eliminar el rol");
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarRol = async (roleId) => {
    if (!window.confirm("¿Seguro deseas eliminar este rol?")) return;
    setLoading(true);
    try {
      await deleteRole(roleId);
      fetchRoles();
    } catch (err) {
      console.error("Error al eliminar rol:", err);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // DERIVADOS
  // =========================
  const usuarioActual = usuarios.find(
    (u) => String(u.id) === String(usuarioSeleccionado)
  );

  // ✅ Verificar si este es el último admin del sistema
  const isLastAdmin = () => {
    const adminCount = usuarios.filter(u => 
      u.roles?.some(r => r.name === 'admin')
    ).length;
    return adminCount <= 1;
  };

  // =========================
  // UI
  // =========================
  if (loading && roles.length === 0 && usuarios.length === 0) {
    return (
      <div className="roles-loading-container">
        <div className="roles-loading-spinner"></div>
        <p>Cargando roles y usuarios...</p>
      </div>
    );
  }

  return (
    <div className="roles-container">
      {/* Header */}
      <div className="roles-header">
        <div className="header-left">
          <div className="header-icon">👥</div>
          <div>
            <h1>Roles y Permisos</h1>
            <p>Gestión de roles de usuario y asignaciones</p>
          </div>
        </div>
      </div>

      <div className="roles-grid">
        {/* Columna izquierda - Crear Rol */}
        <div className="roles-card">
          <div className="card-header">
            <span className="card-icon">➕</span>
            <h2>Nuevo Rol</h2>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label required">
                Nombre del Rol <span className="required-star">*</span>
              </label>
              <input
                type="text"
                placeholder="Ej: administrador, tecnico, vendedor..."
                value={nuevoRol}
                onChange={(e) => setNuevoRol(e.target.value)}
                className="form-input"
                disabled={loading}
              />
            </div>
            <button
              onClick={handleCrearRol}
              disabled={loading || !nuevoRol.trim()}
              className="btn-primary btn-block"
            >
              {loading ? (
                <>
                  <span className="loading-spinner-small"></span>
                  Creando...
                </>
              ) : (
                "Crear Rol"
              )}
            </button>
          </div>
        </div>

        {/* Columna derecha - Asignar/Quitar Rol */}
        <div className="roles-card">
          <div className="card-header">
            <span className="card-icon">🔗</span>
            <h2>Gestionar Roles de Usuario</h2>
          </div>
          <div className="card-body">
            {/* Selector de Usuario */}
            <div className="form-group">
              <label className="form-label required">
                Usuario <span className="required-star">*</span>
              </label>
              <select
                value={usuarioSeleccionado}
                onChange={(e) => setUsuarioSeleccionado(e.target.value)}
                className="form-select"
                disabled={loading}
              >
                <option value="">Selecciona un usuario</option>
                {usuarios.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.username} ({u.email})
                  </option>
                ))}
              </select>
            </div>

            {/* Roles actuales del usuario con botón de eliminar */}
            {usuarioActual && (
              <div className="roles-actuales">
                <span className="roles-label">Roles actuales:</span>
                <div className="roles-tags">
                  {usuarioActual.roles && usuarioActual.roles.length > 0 ? (
                    usuarioActual.roles.map((r) => {
                      const ultimoAdmin = isLastAdmin() && r.name === 'admin';
                      return (
                        <span key={r.id} className="role-tag">
                          {r.name}
                          {ultimoAdmin ? (
                            <span className="role-protected" title="No se puede eliminar el último admin">🔒</span>
                          ) : (
                            <button
                              onClick={() => handleRemoveRoleFromUser(
                                usuarioActual.id,
                                r.id,
                                r.name,
                                usuarioActual.username
                              )}
                              className="role-remove-btn"
                              title={`Eliminar rol ${r.name}`}
                              disabled={loading}
                            >
                              ✕
                            </button>
                          )}
                        </span>
                      );
                    })
                  ) : (
                    <span className="sin-roles">Sin roles asignados</span>
                  )}
                </div>
              </div>
            )}

            {/* Selector de Rol para asignar */}
            <div className="form-group">
              <label className="form-label required">
                Rol a asignar <span className="required-star">*</span>
              </label>
              <select
                value={rolSeleccionado}
                onChange={(e) => setRolSeleccionado(e.target.value)}
                className="form-select"
                disabled={loading}
              >
                <option value="">Selecciona un rol</option>
                {roles.map((r) => {
                  const yaTiene = usuarioActual?.roles?.some(ur => ur.id === r.id);
                  return (
                    <option key={r.id} value={r.id} disabled={yaTiene}>
                      {r.name} {yaTiene ? '(✓ ya asignado)' : ''}
                    </option>
                  );
                })}
              </select>
            </div>

            <button
              onClick={handleAsignarRol}
              disabled={loading || !usuarioSeleccionado || !rolSeleccionado}
              className="btn-success btn-block"
            >
              {loading ? (
                <>
                  <span className="loading-spinner-small"></span>
                  Asignando...
                </>
              ) : (
                "Asignar Rol"
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Lista de Roles Existentes */}
      <div className="roles-list-section">
        <div className="list-header">
          <h3>Roles Existentes</h3>
          <span className="roles-count">{roles.length} roles</span>
        </div>

        {roles.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">👥</div>
            <p className="empty-title">No hay roles registrados</p>
            <p className="empty-subtitle">Crea tu primer rol para comenzar</p>
          </div>
        ) : (
          <div className="roles-list">
            {roles.map((r) => (
              <div key={r.id} className="role-item">
                <div className="role-info">
                  <span className="role-name">{r.name}</span>
                  <span className="role-id">ID: {r.id}</span>
                </div>
                <button
                  onClick={() => handleEliminarRol(r.id)}
                  disabled={loading || r.name === 'admin'}
                  className="role-delete-btn"
                  title={r.name === 'admin' ? 'No se puede eliminar el rol admin' : 'Eliminar rol'}
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Nota informativa */}
      <div className="roles-note">
        <span className="note-icon">💡</span>
        <span className="note-text">
          Los roles determinan los permisos que tiene cada usuario en el sistema.
          Asigna roles cuidadosamente según las responsabilidades de cada usuario.
          <strong> El rol "admin" no puede ser eliminado si es el único admin del sistema.</strong>
        </span>
      </div>
    </div>
  );
}
